import { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon, CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import { inventoryIntegrationApi } from "../../../api/inventory/integration";

export function ReceiptSyncStatus({ receipt, onRefresh }) {
  const { t } = useTranslation();
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canResume = roles.some((role) => role.name === "admin") ||
    ["inventory_settings", "inventory_finance"].every((name) => roles.some((role) => role.name === name));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const sync = receipt.tripletex_sync || {};
  const confirmed = sync.state === "synced" && !!sync.remote_id;
  const needsReview = ["unknown", "failed", "conflict", "unsupported"].includes(sync.state);

  const resume = async () => {
    if (busy) return;
    setBusy(true); setError(false);
    try {
      const response = await inventoryIntegrationApi.resolve(sync.operation_id, { resolution: "resume_receipt" });
      if (!response?.data?.success) throw new Error("Unconfirmed receipt synchronization");
      await onRefresh();
    } catch (_) { setError(true); }
    finally { setBusy(false); }
  };

  return <section aria-label={t("inventoryReceipt.syncTitle")} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex gap-3">
        {confirmed ? <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" /> : <ClockIcon className="h-6 w-6 shrink-0 text-amber-600" />}
        <div><h2 className="font-semibold text-slate-900">{t("inventoryReceipt.localPosted")}</h2>
          <p className="mt-1 text-sm text-slate-600">{confirmed ? t("inventoryReceipt.remoteConfirmed") : t("inventoryReceipt.remotePending")}</p>
          <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${confirmed ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>
            Tripletex · {t(`inventoryPurchase.sync.${sync.state || "not_sent"}`)}
          </span>
        </div>
      </div>
      <button type="button" onClick={onRefresh} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 disabled:opacity-50"><ArrowPathIcon className="h-4 w-4" />{t("inventoryValuation.refresh")}</button>
    </div>
    {needsReview && <div className="mt-4 border-t border-slate-100 pt-4 text-sm text-slate-600">
      <p>{t("inventoryReceipt.resumeHint")}</p>
      {canResume && sync.operation_id && <button type="button" onClick={resume} disabled={busy} className="mt-3 min-h-[44px] rounded-xl bg-[#1463C7] px-4 font-semibold text-white disabled:opacity-50">{t(busy ? "inventoryReceipt.checkingSync" : "inventoryReceipt.resumeSync")}</button>}
    </div>}
    {error && <p role="alert" className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{t("inventoryReceipt.resumeFailed")}</p>}
  </section>;
}
